import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

export interface ConfirmDeleteDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  isPending: boolean;
  error: unknown;
  /** Replaces the generic text for 409 RESOURCE_IN_USE with what to do about it. */
  inUseMessage?: string;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/** Stays open until the backend answers so a 409 RESOURCE_IN_USE is read where it happened. */
export function ConfirmDeleteDialog({
  open,
  title,
  description,
  confirmLabel,
  isPending,
  error,
  inUseMessage,
  onConfirm,
  onOpenChange,
}: ConfirmDeleteDialogProps) {
  const { t } = useTranslation(['common', 'errors']);
  const message = useErrorMessage();
  const requestId = isApiError(error) ? error.requestId : undefined;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {inUseMessage && isApiError(error) && error.code === 'RESOURCE_IN_USE'
                ? inUseMessage
                : message(error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
